import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { Comment } from 'src/app/models/comment.model';
import { BusinessPurpose, Ticket } from 'src/app/models/ticket/ticket.model';
import { User } from 'src/app/models/user.model';
import { Permission } from 'src/app/shared/constants/permissions';
import * as fromStore from '../../../store';
import { CommentService } from '../../services';

@Component({
  selector: 'app-ticket-comment',
  templateUrl: './ticket-comment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketCommentComponent implements OnChanges {
  @Input() ticket: Ticket;

  @Input() fileUploadResult: {
    files: { fileName: string; success: boolean }[];
  };

  @Output() saved = new EventEmitter<void>();

  @ViewChild('commentsContainer', { static: false })
  commentsContainer: ElementRef;

  commentForm: UntypedFormGroup;

  readonly isCommentSubmitting = signal(false);

  readonly isCommentEmpty = signal(true);

  readonly hasError = signal(false);

  readonly isTicketIdOnRouter =
    !!inject(ActivatedRoute).snapshot.paramMap.get('ticketId');

  readonly Permission = Permission;

  readonly BusinessPurpose = BusinessPurpose;

  private readonly store = inject<Store<fromStore.State>>(Store);

  private readonly destroyRef = inject(DestroyRef);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly commentService = inject(CommentService);

  private readonly fb = inject(UntypedFormBuilder);

  private readonly currentUser = toSignal<User | null>(
    this.store.select(fromStore.getCurrentUser),
    { initialValue: null },
  );

  readonly currentUserInitials = computed(() => {
    const user = this.currentUser();
    if (!user) return '';
    return this.computeInitials(
      [user.FirstName, user.LastName].filter(Boolean).join(' '),
    );
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.ticket?.Id && this.isTicketIdOnRouter) {
      this.onSmallScreen();
    }
    this.populateCommentForm();
    this.fileUploadResultChange(changes);
  }

  onSmallScreen(): void {
    this.store
      .select(fromStore.getCurrentRouterTicket)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ticket) => {
        if (!ticket?.Id) return;
        this.ticket = ticket;
        this.cdr.markForCheck();
      });

    setTimeout(() => this.markCommentsAsRead(), 5000);
  }

  populateCommentForm(): void {
    this.commentForm = this.fb.group({
      Case__c: [this.ticket ? `${this.ticket.Id}` : ''],
      Body__c: ['', [Validators.required]],
    });
  }

  markCommentsAsRead(): void {
    if (
      !this.ticket?.Id ||
      !this.ticket.Comments__r?.totalSize ||
      !this.hasUnreadComments(this.ticket.Comments__r.records)
    ) {
      return;
    }
    firstValueFrom(
      this.commentService.markCommentsAsRead({ Case__c: this.ticket.Id }),
    )
      .then(() => {
        this.store.dispatch(new fromStore.ClearTickets());
        this.store.dispatch(new fromStore.LoadTickets());
        this.refreshRouteTicket();
      })
      .catch(() => {});
  }

  hasUnreadComments = (comments: Comment[]): boolean =>
    comments.some((c) => !c.Is_Read__c);

  saveFormComment(form: UntypedFormGroup): void {
    if (!this.isCommentEmpty() && this.commentForm.valid) {
      this.saveComment(form.value);
    }
  }

  private saveComment(comment: { Case__c: string; Body__c: string }): void {
    this.isCommentSubmitting.set(true);
    this.commentForm.disable();

    this.commentService.createComment(comment).subscribe({
      next: (data) => {
        this.isCommentSubmitting.set(false);
        this.commentForm.enable();
        if (data?.isSuccess) {
          this.isCommentEmpty.set(true);
          this.commentForm.reset({ Case__c: this.ticket.Id, Body__c: '' });
        }
        this.store.dispatch(new fromStore.LoadTickets());
        if (this.isTicketIdOnRouter) {
          this.onSmallScreen();
        }
        this.refreshRouteTicket();
        this.saved.emit();
      },
      error: () => {
        this.isCommentSubmitting.set(false);
        this.commentForm.enable();
        this.hasError.set(true);
      },
    });
  }

  onCommentChanges(value: string): void {
    this.isCommentEmpty.set(value.replace(/\s+/g, '').length <= 1);
  }

  private refreshRouteTicket(): void {
    if (this.isTicketIdOnRouter && !this.ticket?.Id) {
      this.onSmallScreen();
    }
  }

  getCommentInitials(comment: Comment): string {
    return this.computeInitials(this.resolveCommentAuthorName(comment));
  }

  isSystemComment(comment: Comment): boolean {
    return this.resolveCommentAuthorName(comment) === 'Comcast Business';
  }

  isMyComment(comment: Comment): boolean {
    const user = this.currentUser();
    if (!user) return false;
    const authorName = this.resolveCommentAuthorName(comment);
    if (authorName === 'Comcast Business') return false;
    const myName = [user.FirstName, user.LastName].filter(Boolean).join(' ');
    return authorName.toLowerCase() === myName.toLowerCase();
  }

  private resolveCommentAuthorName(comment: Comment): string {
    const systemNames = [
      'Api',
      'API',
      'My',
      'Salesforce',
      'System User',
      'System',
    ];

    if (comment?.Contact__r?.Name && !comment?.User__r?.FirstName) {
      return systemNames.includes(comment.Contact__r.Name)
        ? 'Comcast Business'
        : comment.Contact__r.Name;
    }
    if (!comment?.User__r?.FirstName && !comment?.Contact__r?.Name) {
      const first = comment?.CreatedBy?.FirstName ?? '';
      return systemNames.includes(first) ? 'Comcast Business' : first;
    }
    if (!comment?.Contact__r?.Name && comment?.User__r?.FirstName) {
      return comment.User__r.FirstName === 'System'
        ? 'Comcast Business'
        : comment.User__r.FirstName;
    }
    return '';
  }

  private computeInitials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }

  private fileUploadResultChange(changes: SimpleChanges): void {
    const oldName =
      changes.fileUploadResult?.previousValue?.files?.[0]?.fileName ?? '';
    const newName =
      changes.fileUploadResult?.currentValue?.files?.[0]?.fileName ?? '';
    const success =
      changes.fileUploadResult?.currentValue?.files?.[0]?.success ?? false;

    if (newName !== oldName && success) {
      this.saveComment({
        Case__c: this.ticket.Id,
        Body__c: `File '${newName}' added.`,
      });
    }
  }
}
